import type {
  AuthResponse,
  KuboResponse,
  LobbyResponse,
  MeResponse,
  PurchaseResponse,
  ResponsibleLimits,
  VenueTablesResponse,
  WalletResponse,
} from '@tira/protocol';

import { SERVER_ORIGIN } from './origin.js';

const TOKEN_KEY = 'tpm.token';
const DEVICE_KEY = 'tpm.device';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

/** Opaque per-install id, used only for abuse throttling — never PII. */
export function deviceId(): string {
  let d = localStorage.getItem(DEVICE_KEY);
  if (!d) {
    d = crypto.randomUUID();
    localStorage.setItem(DEVICE_KEY, d);
  }
  return d;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const res = await fetch(`${SERVER_ORIGIN}/api${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { code?: string; message?: string };
    throw new ApiError(res.status, body.code ?? 'internal', body.message ?? res.statusText);
  }
  return (await res.json()) as T;
}

export const api = {
  guest: (handle?: string) =>
    req<AuthResponse>('/auth/guest', {
      method: 'POST',
      body: JSON.stringify({ deviceId: deviceId(), ...(handle ? { handle } : {}) }),
    }),
  me: () => req<MeResponse>('/me'),
  /** The 18+ self-attestation hard gate (S2). */
  acceptAgeGate: () => req<MeResponse>('/me/age-gate', { method: 'POST' }),
  setLimits: (limits: Partial<ResponsibleLimits>) =>
    req<ResponsibleLimits>('/me/limits', { method: 'POST', body: JSON.stringify(limits) }),
  deleteAccount: () => req<{ ok: true }>('/me', { method: 'DELETE' }),

  wallet: (cursor?: string) => req<WalletResponse>(`/wallet${cursor ? `?cursor=${cursor}` : ''}`),
  claimBaon: () => req<{ credited: number; balance: number; streak: number }>('/bonus/baon', { method: 'POST' }),
  claimRescue: () => req<{ credited: number; balance: number }>('/bonus/rescue', { method: 'POST' }),

  lobby: () => req<LobbyResponse>('/lobby'),
  /** The rooms at one venue, for picking a table instead of being given one. */
  venueTables: (game: string) => req<VenueTablesResponse>(`/venues/${game}/tables`),
  createKubo: (game: string, tier: string, buyIn: number) =>
    req<KuboResponse>('/kubo', { method: 'POST', body: JSON.stringify({ game, tier, buyIn }) }),
  joinKubo: (code: string) => req<KuboResponse>('/kubo/join', { method: 'POST', body: JSON.stringify({ code }) }),

  packs: () => req<{ packs: { productId: string; chips: number; pricePhp: number; bestValue: boolean }[] }>('/store/packs'),
  purchase: (productId: string) =>
    req<PurchaseResponse>('/store/purchase', {
      method: 'POST',
      // Sandbox path only. On device the store plugin supplies a real receipt
      // and the server validates it — the client never credits itself (PRD §10).
      body: JSON.stringify({ productId, receipt: `sandbox:${crypto.randomUUID()}`, platform: 'sandbox' }),
    }),

  /** Commit-reveal verifier data for the Fair Play screen (PRD §8). */
  fairness: (matchId: string) =>
    req<{ commit: string; seed: string | null; algorithm: string; verified: boolean | null }>(
      `/matches/${matchId}/fairness`,
    ),
};
